import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import DateRangeIcon from '@mui/icons-material/DateRange';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

// Número da semana no padrão ISO: a semana vai de segunda a domingo e a semana 1 é a que tem a
// primeira quinta-feira do ano (é o mesmo número do calendário do Outlook/Teams).
dayjs.extend(isoWeek);

const CATEGORIA_CONFIG = {
  consulting:  { label: 'Consulting',  cor: '#EEEDFE', textColor: '#3C3489' },
  business:    { label: 'Business',    cor: '#FDEBD3', textColor: '#8A5A00' },
  management:  { label: 'Management',  cor: '#D6EAF8', textColor: '#1A5276' },
  development: { label: 'Development', cor: '#D5F5E3', textColor: '#186A3B' },
  logistica:   { label: 'Logística',   cor: '#F5D9EC', textColor: '#8A2D6F' },
  folga:       { label: 'Feriado / Folga', cor: '#E8E8E8', textColor: '#666666' },
};

const FORM_VAZIO = {
  periodo: 'dia_inteiro',
  categoria: 'consulting', cliente: '', projeto: '', codigo_atividade: '',
  tipo_carga: 'consultoria', workshop: '',
};

const EXISTENTES_VAZIO = { dia_inteiro: null, manha: null, tarde: null };

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

export default function Agenda() {
  const { temAcesso, isSuperAdmin, perfil } = useAuth();
  const acessoAmpliado = temAcesso('agenda');

  const [mes, setMes] = useState(dayjs().month() + 1);
  const [ano, setAno] = useState(dayjs().year());
  // Filtro de semana: null = mês inteiro; ou a segunda-feira (AAAA-MM-DD) da semana escolhida.
  const [semanaInicio, setSemanaInicio] = useState(null);
  const [membros, setMembros] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  // O que a pessoa pode fazer na Agenda (vem do backend). null = não conseguiu carregar:
  // nesse caso a tela se comporta como antes e é o servidor que barra o que não pode.
  const [permissao, setPermissao] = useState(null);
  const [codigos, setCodigos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  // Aviso do servidor quando o lançamento de Workshop foi salvo sem consumir nenhum workshop
  const [aviso, setAviso] = useState('');
  // Para a escolha do workshop: tipos do catálogo (Admin > Tipos de workshop), workshops em andamento
  // do projeto/contrato (continuar o dia 2...) e quantos vendidos ainda estão livres.
  const SEM_WORKSHOPS = { tipos: [], em_andamento: [], livres: 0, total: 0 };
  const [workshopsDoProjeto, setWorkshopsDoProjeto] = useState(SEM_WORKSHOPS);

  const [celulaAberta, setCelulaAberta] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [ofertaOutroPeriodo, setOfertaOutroPeriodo] = useState(null);
  // Workshop de vários dias salvo com dias faltando: { workshop: 'w:ID', nome, faltam, form }
  const [ofertaProximoDia, setOfertaProximoDia] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroDialog, setErroDialog] = useState('');

  const diasNoMes = dayjs(`${ano}-${String(mes).padStart(2, '0')}-01`).daysInMonth();

  // Cada coluna da tabela é um dia: { ds: 'AAAA-MM-DD', d: dia do mês, dow: dia da semana (0=dom) }.
  // Com o filtro de semana ligado são os 7 dias da semana (podem ser de dois meses); senão, o mês todo.
  const montaColuna = (data) => ({ ds: data.format('YYYY-MM-DD'), d: data.date(), dow: data.day() });
  const inicioSemanaEscolhida = semanaInicio ? dayjs(semanaInicio) : null;
  const colunas = inicioSemanaEscolhida
    ? Array.from({ length: 7 }, (_, i) => montaColuna(inicioSemanaEscolhida.add(i, 'day')))
    : Array.from({ length: diasNoMes }, (_, i) => montaColuna(dayjs(`${ano}-${String(mes).padStart(2, '0')}-01`).add(i, 'day')));

  const hojeStr = dayjs().format('YYYY-MM-DD');
  const ehFimDeSemana = (c) => c.dow === 0 || c.dow === 6;
  const ehHoje = (c) => c.ds === hojeStr;
  const ehSegunda = (c) => c.dow === 1;

  // ---- Número da semana (ISO) ----
  // Semana de hoje (para a etiqueta ao lado do mês e para destacar na faixa).
  const hojeDayjs = dayjs();
  const inicioSemanaHoje = hojeDayjs.startOf('isoWeek');
  const fimSemanaHoje = hojeDayjs.endOf('isoWeek');
  const mesCurto = (d) => MESES[d.month()].slice(0, 3).toLowerCase();
  const rotuloSemanaHoje = inicioSemanaHoje.month() === fimSemanaHoje.month()
    ? `${inicioSemanaHoje.date()}–${fimSemanaHoje.date()} ${mesCurto(fimSemanaHoje)}`
    : `${inicioSemanaHoje.date()} ${mesCurto(inicioSemanaHoje)}–${fimSemanaHoje.date()} ${mesCurto(fimSemanaHoje)}`;

  // Semanas que aparecem na tabela: número, quantos dias dela aparecem e se é a semana de hoje.
  const semanasDoMes = [];
  colunas.forEach((c) => {
    const data = dayjs(c.ds);
    const numero = data.isoWeek();
    const ultima = semanasDoMes[semanasDoMes.length - 1];
    if (ultima && ultima.numero === numero) {
      ultima.dias += 1;
    } else {
      semanasDoMes.push({ numero, dias: 1, atual: data.startOf('isoWeek').isSame(inicioSemanaHoje, 'day') });
    }
  });

  // Lista do filtro: todas as semanas (ISO) do ano escolhido, como a lista de meses mostra todos os meses.
  const primeiraSegundaDoAno = dayjs(`${ano}-01-04`).startOf('isoWeek');
  const totalSemanasDoAno = dayjs(`${ano}-12-28`).isoWeek();
  const semanasDoAno = Array.from({ length: totalSemanasDoAno }, (_, i) => {
    const ini = primeiraSegundaDoAno.add(i, 'week');
    const fim = ini.add(6, 'day');
    const intervalo = ini.month() === fim.month()
      ? `${ini.date()}–${fim.date()} ${mesCurto(fim)}`
      : `${ini.date()} ${mesCurto(ini)}–${fim.date()} ${mesCurto(fim)}`;
    return { numero: i + 1, valor: ini.format('YYYY-MM-DD'), rotulo: `Semana ${i + 1} (${intervalo})` };
  });
  const nomeDiaSemana = (dow) => ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][dow];

  // Escolheu uma semana: o mês/ano ao lado passam a ser os da quinta-feira dela (regra ISO).
  const escolherSemana = (valor) => {
    if (valor === 'todas') { setSemanaInicio(null); return; }
    const quinta = dayjs(valor).add(3, 'day');
    setSemanaInicio(valor);
    setMes(quinta.month() + 1);
    setAno(quinta.year());
  };

  // Meses que precisam ser buscados: o mês aberto e, se a semana escolhida atravessa dois meses, os dois.
  const mesesParaBuscar = [{ ano, mes }];
  if (inicioSemanaEscolhida) {
    [inicioSemanaEscolhida, inicioSemanaEscolhida.add(6, 'day')].forEach((x) => {
      if (!mesesParaBuscar.some((m) => m.ano === x.year() && m.mes === x.month() + 1)) {
        mesesParaBuscar.push({ ano: x.year(), mes: x.month() + 1 });
      }
    });
  }
  const chaveMeses = mesesParaBuscar.map((m) => `${m.ano}-${m.mes}`).join(',');

  const irParaMesAnterior = () => {
    setSemanaInicio(null);
    if (mes === 1) { setMes(12); setAno(ano - 1); } else { setMes(mes - 1); }
  };
  const irParaProximoMes = () => {
    setSemanaInicio(null);
    if (mes === 12) { setMes(1); setAno(ano + 1); } else { setMes(mes + 1); }
  };
  const irParaHoje = () => { setSemanaInicio(null); setMes(dayjs().month() + 1); setAno(dayjs().year()); };

  const formDoExistente = (existente, periodo) => ({
    periodo,
    categoria: existente?.categoria || 'consulting',
    cliente: existente?.cliente || '',
    projeto: existente?.projeto || '',
    codigo_atividade: existente?.codigo_atividade || '',
    tipo_carga: existente?.tipo_carga || 'consultoria',
    workshop: existente?.workshop ? `w:${existente.workshop}` : '',
  });

  const periodoInicial = (existentes) => {
    if (existentes.dia_inteiro) return 'dia_inteiro';
    if (existentes.manha) return 'manha';
    if (existentes.tarde) return 'tarde';
    return 'dia_inteiro';
  };

  const handleCheckinDoDia = async () => {
    if (!perfil) {
      setErro('Seu usuário não tem um cadastro de Membro da Equipe vinculado, então não é possível fazer Check-in. Fale com o Admin do sistema.');
      return;
    }
    const hoje = dayjs();
    const mesHoje = hoje.month() + 1;
    const anoHoje = hoje.year();
    const diaHoje = hoje.date();
    const precisaTrocarMes = mes !== mesHoje || ano !== anoHoje;

    if (precisaTrocarMes) setSemanaInicio(null);
    if (mes !== mesHoje) setMes(mesHoje);
    if (ano !== anoHoje) setAno(anoHoje);

    if (!precisaTrocarMes) {
      handleAbrirCelula(perfil, hoje.format('YYYY-MM-DD'));
      return;
    }

    setErro('');
    try {
      const res = await api.get(`/checkins/?mes=${mesHoje}&ano=${anoHoje}`);
      const doDia = res.data.filter(
        (c) => c.membro_equipe === perfil.id && dayjs(c.data).date() === diaHoje
      );
      const existentes = { ...EXISTENTES_VAZIO };
      doDia.forEach((c) => { existentes[c.periodo || 'dia_inteiro'] = c; });
      const dataStr = `${anoHoje}-${String(mesHoje).padStart(2, '0')}-${String(diaHoje).padStart(2, '0')}`;
      const inicial = periodoInicial(existentes);
      setCelulaAberta({ membroEquipe: perfil, dia: diaHoje, dataStr, existentes });
      setForm(formDoExistente(existentes[inicial], inicial));
      setOfertaOutroPeriodo(null);
      setErroDialog('');
    } catch {
      setErro('Erro ao abrir o check-in de hoje. Tenta de novo.');
    }
  };

  const carregar = () => {
    setLoading(true);
    setErro('');
    const buscaMembros = acessoAmpliado
      ? api.get('/membros-equipe/')
      : Promise.resolve({ data: perfil ? [perfil] : [] });
    const buscaPermissao = api.get('/checkins/minha-agenda/').catch(() => ({ data: null }));

    Promise.all([
      buscaMembros,
      Promise.all(mesesParaBuscar.map((m) => api.get(`/checkins/?mes=${m.mes}&ano=${m.ano}`)))
        .then((respostas) => ({ data: respostas.flatMap((r) => r.data) })),
      api.get('/clientes/'),
      api.get('/projetos/'),
      api.get('/codigos-atividade/'),
      buscaPermissao,
    ]).then(([mem, chk, cli, proj, cod, perm]) => {
      // Quem assiste a agenda de alguém (ex: assistente do Presidente) vê essa pessoa
      // na tela mesmo sem o módulo Membros da Equipe.
      const lista = mem.data.filter((c) => c.ativo !== false);
      [...(perm.data?.membros || []), ...(perm.data?.assistidos || [])].forEach((a) => {
        if (!lista.some((m) => m.id === a.id)) lista.push(a);
      });
      setPermissao(perm.data);
      setMembros(lista);
      setCheckins(chk.data);
      setClientes(cli.data.filter((c) => c.codigo_agenda));
      setProjetos(proj.data);
      setCodigos(cod.data);
      setLoading(false);
    }).catch(() => {
      setErro('Erro ao carregar a Agenda.');
      setLoading(false);
    });
  };

  useEffect(() => { carregar(); }, [chaveMeses, acessoAmpliado]);

  const projetoDoForm = form.categoria === 'consulting' && form.tipo_carga === 'workshop' ? form.projeto : '';
  useEffect(() => {
    if (!celulaAberta || !projetoDoForm) { setWorkshopsDoProjeto(SEM_WORKSHOPS); return; }
    let ativo = true;
    api.get('/checkins/workshops-do-projeto/', { params: { projeto: projetoDoForm } })
      .then((res) => { if (ativo) setWorkshopsDoProjeto(res.data); })
      .catch(() => { if (ativo) setWorkshopsDoProjeto(SEM_WORKSHOPS); });
    return () => { ativo = false; };
  }, [celulaAberta, projetoDoForm]);

  // Fechou a janela (Cancelar, Fechar ou clicando fora): a oferta do próximo dia não vale mais.
  useEffect(() => { if (!celulaAberta) setOfertaProximoDia(null); }, [celulaAberta]);

  const mapaCheckins = {};
  checkins.forEach((c) => {
    const chave = `${c.membro_equipe}-${c.data}`;
    if (!mapaCheckins[chave]) mapaCheckins[chave] = { ...EXISTENTES_VAZIO };
    mapaCheckins[chave][c.periodo || 'dia_inteiro'] = c;
  });

  // Assistente lançando na agenda de outra pessoa: soma os clientes liberados pela agenda
  // (a lista normal só traz os clientes dos projetos da própria pessoa logada).
  const clientesDisponiveis = [
    ...clientes,
    ...(permissao?.clientes || []).filter((c) => !clientes.some((x) => x.id === c.id)),
  ].sort((a, b) => (a.nome || '').localeCompare(b.nome || ''));

  // ---- Escolha do workshop (lançamento de Workshop) ----
  // O catálogo de workshops é cadastrado no Admin (nome + dias). Aqui a pessoa escolhe pelo nome:
  //  - "Novo": começa um workshop do catálogo (gasta 1 dos vendidos do contrato);
  //  - "Continuar": lança outro dia de um workshop que já foi iniciado (não gasta outro).
  const rotuloDias = (n) => `${n} ${n === 1 ? 'dia' : 'dias'}`;
  const opcoesNovo = (workshopsDoProjeto.tipos || []).map((t) => ({
    valor: `t:${t.id}`, dias: t.dias, nome: t.nome,
    rotulo: `Novo: ${t.nome} (${rotuloDias(t.dias)})`,
  }));
  const opcoesContinuar = (workshopsDoProjeto.em_andamento || []).map((w) => {
    const datas = (w.datas || []).map((d) => dayjs(d).format('DD/MM')).join(', ');
    const quem = w.membros?.length ? `, ${w.membros.join(', ')}` : '';
    return {
      valor: `w:${w.id}`, dias: w.dias, nome: w.nome,
      rotulo: `Continuar: ${w.nome} — dia ${w.dias_lancados + 1} de ${w.dias} (iniciado em ${datas}${quem})`,
    };
  });
  const opcoesWorkshop = [...opcoesContinuar, ...opcoesNovo];
  // Lançamento já salvo com um workshop que não está mais "em andamento" (ex: concluído): mantém a opção.
  const existenteWorkshop = celulaAberta?.existentes?.[form.periodo];
  if (existenteWorkshop?.workshop && !opcoesWorkshop.some((o) => o.valor === `w:${existenteWorkshop.workshop}`)) {
    opcoesWorkshop.unshift({
      valor: `w:${existenteWorkshop.workshop}`, dias: 1, nome: existenteWorkshop.workshop_nome,
      rotulo: `${existenteWorkshop.workshop_nome} (já lançado)`,
    });
  }
  const opcaoEscolhida = opcoesWorkshop.find((o) => o.valor === form.workshop);
  const precisaEscolherWorkshop = opcoesNovo.length > 0 || opcoesContinuar.length > 0;

  const rotuloCelula = (checkin) => {
    if (!checkin) return '';
    if (checkin.categoria === 'consulting') {
      const cli = clientesDisponiveis.find((c) => c.id === checkin.cliente);
      return cli ? cli.codigo_agenda : (checkin.cliente_nome || 'C').slice(0, 3).toUpperCase();
    }
    if (checkin.categoria === 'folga') return 'F';
    if (checkin.categoria === 'logistica') return 'LOG';
    return checkin.codigo_atividade_codigo || '?';
  };

  // Pode lançar/editar a agenda dessa linha? (o servidor confere de novo ao salvar)
  const podeEditarLinha = (membro) => (
    !permissao || permissao.todos || permissao.editaveis.includes(membro.id)
  );

  const handleAbrirCelula = (membroEquipe, dataStr) => {
    if (!podeEditarLinha(membroEquipe)) return;
    const dia = dayjs(dataStr).date();
    const existentes = mapaCheckins[`${membroEquipe.id}-${dataStr}`] || { ...EXISTENTES_VAZIO };
    const inicial = periodoInicial(existentes);
    setCelulaAberta({ membroEquipe, dia, dataStr, existentes });
    setForm(formDoExistente(existentes[inicial], inicial));
    setOfertaOutroPeriodo(null);
    setOfertaProximoDia(null);
    setErroDialog('');
  };

  const handleMudarPeriodo = (novoPeriodo) => {
    const existente = celulaAberta.existentes[novoPeriodo] || null;
    setForm(formDoExistente(existente, novoPeriodo));
    setErroDialog('');
  };

  const handleSalvarCelula = async () => {
    setSalvando(true);
    setErroDialog('');
    const ehConsulting = form.categoria === 'consulting';
    if (ehConsulting && !form.projeto) {
      setErroDialog('Escolha o projeto para salvar um lançamento de Consulting.');
      setSalvando(false);
      return;
    }
    const existenteDoPeriodo = celulaAberta.existentes[form.periodo];
    if (ehConsulting && form.tipo_carga === 'workshop' && precisaEscolherWorkshop && !form.workshop && !existenteDoPeriodo) {
      setErroDialog('Escolha o workshop: "Novo" para começar um, ou "Continuar" para lançar outro dia de um que já começou.');
      setSalvando(false);
      return;
    }
    const payload = {
      membro_equipe: celulaAberta.membroEquipe.id,
      data: celulaAberta.dataStr,
      periodo: form.periodo,
      categoria: form.categoria,
      cliente: ehConsulting ? (form.cliente || null) : null,
      projeto: ehConsulting ? (form.projeto || null) : null,
      codigo_atividade: ['business', 'management', 'development'].includes(form.categoria)
        ? (form.codigo_atividade || null) : null,
      tipo_carga: ehConsulting ? form.tipo_carga : 'consultoria',
      // "w:<id>" = continuar um workshop em andamento; "t:<id>" = começar um workshop novo do catálogo.
      workshop: ehConsulting && form.tipo_carga === 'workshop' && String(form.workshop).startsWith('w:')
        ? Number(String(form.workshop).slice(2)) : null,
      tipo_workshop: ehConsulting && form.tipo_carga === 'workshop' && String(form.workshop).startsWith('t:')
        ? Number(String(form.workshop).slice(2)) : null,
    };
    const existenteAtual = celulaAberta.existentes[form.periodo];
    try {
      const res = existenteAtual
        ? await api.patch(`/checkins/${existenteAtual.id}/`, payload)
        : await api.post('/checkins/', payload);

      const salvo = res.data;
      const existentesAtualizados = { ...celulaAberta.existentes, [form.periodo]: salvo };
      setCelulaAberta((prev) => ({ ...prev, existentes: existentesAtualizados }));
      setSucesso('Agenda atualizada!');
      setAviso(salvo.aviso_workshop || '');

      const outroPeriodo = form.periodo === 'manha' ? 'tarde' : form.periodo === 'tarde' ? 'manha' : null;
      const outroLivre = outroPeriodo && !existentesAtualizados[outroPeriodo];
      if (ehConsulting && form.tipo_carga === 'workshop' && salvo.workshop && salvo.workshop_dias_faltam > 0) {
        // Workshop de vários dias com dia(s) faltando: oferece lançar o próximo dia útil já como "Continuar".
        setOfertaOutroPeriodo(null);
        setOfertaProximoDia({
          workshop: `w:${salvo.workshop}`, nome: salvo.workshop_nome, faltam: salvo.workshop_dias_faltam,
          form: { ...form },
        });
      } else if (outroLivre) {
        setOfertaOutroPeriodo(outroPeriodo);
      } else {
        setCelulaAberta(null);
      }
      carregar();
    } catch (err) {
      const detalhe = err?.response?.data;
      setErroDialog(
        detalhe?.detail ||
        (detalhe && typeof detalhe === 'object' ? Object.values(detalhe)[0] : null) ||
        'Erro ao salvar.'
      );
    } finally {
      setSalvando(false);
    }
  };

  // Próximo dia útil (segunda a sexta) depois de `dataStr` em que a pessoa ainda não tem lançamento.
  // Feriados não são conhecidos: se cair num feriado, é só escolher outro dia na Agenda.
  const proximoDiaLivre = (membroId, dataStr) => {
    let d = dayjs(dataStr);
    for (let i = 0; i < 15; i += 1) {
      d = d.add(1, 'day');
      if (d.day() === 0 || d.day() === 6) continue;
      const chave = `${membroId}-${d.format('YYYY-MM-DD')}`;
      const ocupado = mapaCheckins[chave] && Object.values(mapaCheckins[chave]).some(Boolean);
      if (!ocupado) return d;
    }
    return dayjs(dataStr).add(1, 'day');
  };

  const handleLancarProximoDia = () => {
    if (!ofertaProximoDia) return;
    const membro = celulaAberta.membroEquipe;
    const d = proximoDiaLivre(membro.id, celulaAberta.dataStr);
    const dataStr = d.format('YYYY-MM-DD');
    if (d.month() + 1 !== mes || d.year() !== ano) { setSemanaInicio(null); setMes(d.month() + 1); setAno(d.year()); }
    const f = ofertaProximoDia.form;
    setCelulaAberta({ membroEquipe: membro, dia: d.date(), dataStr, existentes: { ...EXISTENTES_VAZIO } });
    setForm({
      ...formDoExistente(null, f.periodo),
      categoria: 'consulting', cliente: f.cliente, projeto: f.projeto,
      tipo_carga: 'workshop', workshop: ofertaProximoDia.workshop,
    });
    setOfertaProximoDia(null);
    setOfertaOutroPeriodo(null);
    setErroDialog('');
  };

  const handleRemoverCelula = async () => {
    const existenteAtual = celulaAberta?.existentes?.[form.periodo];
    if (!existenteAtual) return;
    setSalvando(true);
    try {
      await api.delete(`/checkins/${existenteAtual.id}/`);
      setSucesso('Lançamento removido.');
      setCelulaAberta((prev) => ({ ...prev, existentes: { ...prev.existentes, [form.periodo]: null } }));
      setForm(formDoExistente(null, form.periodo));
      carregar();
    } catch {
      setErroDialog('Erro ao remover.');
    } finally {
      setSalvando(false);
    }
  };

  const codigosFiltrados = codigos.filter((c) => c.categoria === form.categoria);
  // Quem tem "Pode lançar em qualquer projeto" (ex: Presidente) escolhe entre todos os projetos,
  // sem precisar estar na equipe de cada um.
  const veTodosProjetos = Boolean(
    celulaAberta && permissao?.projetos_liberados_para?.includes(celulaAberta.membroEquipe.id)
  );
  const projetosDisponiveis = veTodosProjetos ? permissao.projetos : projetos;
  // Projeto concluído ou cancelado sai da lista. Só continua aparecendo quando o lançamento
  // que está aberto já é dele (assim dá para ver e corrigir lançamentos antigos).
  const projetosAtivos = projetosDisponiveis.filter(
    (p) => !['concluido', 'cancelado'].includes(p.status) || p.id === form.projeto,
  );
  const projetosFiltrados = form.cliente
    ? projetosAtivos.filter((p) => p.cliente === form.cliente)
    : projetosAtivos;

  if (loading) return <Carregando />;

  return (
    <Box>
      <PageHeader titulo="Agenda" />
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        {permissao?.todos
          ? 'Clique em um dia para lançar Consulting, Business, Management, Development, Logística ou Feriado / Folga'
          : `Você vê a agenda de todos, mas só lança na sua${permissao?.assistidos?.length ? ' e na de quem você assiste' : ''}. Clique em um dia da sua linha para lançar.`}
      </Typography>

      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
      {aviso && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso('')}>{aviso}</Alert>}
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      <Card sx={{ mb: 2.5, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={irParaMesAnterior} sx={{ color: '#13375F' }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography sx={{ fontWeight: 700, fontSize: 16, minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>
              {MESES[mes - 1]} {ano}
            </Typography>
            <IconButton size="small" onClick={irParaProximoMes} sx={{ color: '#13375F' }}>
              <ChevronRightIcon />
            </IconButton>
            <Box
              sx={{
                ml: 1.5, display: 'inline-flex', alignItems: 'center', gap: 0.6,
                backgroundColor: '#fdecef', color: '#e94560', border: '1px solid #f6c3cc',
                borderRadius: 5, px: 1.5, py: 0.4, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
              }}
            >
              <DateRangeIcon sx={{ fontSize: 15 }} />
              Semana {hojeDayjs.isoWeek()} · {rotuloSemanaHoje}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small" variant="contained"
              startIcon={<EventAvailableIcon sx={{ fontSize: 17 }} />}
              onClick={handleCheckinDoDia}
              sx={{ backgroundColor: '#e94560', '&:hover': { backgroundColor: '#c73550' } }}
            >
              Check-in do dia
            </Button>
            <Button
              size="small" variant="outlined"
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              onClick={irParaHoje}
              disabled={semanaInicio === null && mes === dayjs().month() + 1 && ano === dayjs().year()}
              sx={{
                borderColor: '#13375F', color: '#13375F', fontWeight: 600,
                '&:hover': { borderColor: '#13375F', backgroundColor: 'rgba(19, 55, 95, 0.06)' },
                '&.Mui-disabled': { borderColor: '#cbd5e1', color: '#a0aec0' },
              }}
            >
              Limpar filtros
            </Button>
            <TextField select size="small" label="Mês" value={mes} onChange={(e) => { setSemanaInicio(null); setMes(Number(e.target.value)); }} sx={{ width: 130 }}>
              {MESES.map((m, i) => <MenuItem key={i} value={i + 1}>{m}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Ano" type="number" value={ano} onChange={(e) => { setSemanaInicio(null); setAno(Number(e.target.value)); }} sx={{ width: 90 }} />
            <TextField
              select size="small" label="Semana" sx={{ width: 265 }}
              value={semanaInicio || 'todas'}
              onChange={(e) => escolherSemana(e.target.value)}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 360 } } } }}
            >
              <MenuItem value="todas">Mês inteiro</MenuItem>
              {semanasDoAno.map((s) => (
                <MenuItem key={s.valor} value={s.valor}>{s.rotulo}</MenuItem>
              ))}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {Object.entries(CATEGORIA_CONFIG).map(([key, cfg]) => (
          <Box
            key={key}
            sx={{
              backgroundColor: cfg.cor, color: cfg.textColor,
              px: 1.4, py: 0.5, borderRadius: 5,
              fontSize: 12, fontWeight: 700,
            }}
          >
            {cfg.label}
          </Box>
        ))}
      </Box>

      <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <CardContent sx={{ overflowX: 'auto', p: 0, '&:last-child': { pb: 0 } }}>
          <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 160 + colunas.length * 34, width: '100%' }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{
                  position: 'sticky', left: 0, backgroundColor: '#0d2642', color: '#cfe0f3',
                  padding: '5px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, zIndex: 2,
                }}>
                  Semana do ano
                </Box>
                {semanasDoMes.map((s) => (
                  <Box key={s.numero} component="th" colSpan={s.dias} sx={{
                    backgroundColor: s.atual ? '#e94560' : '#0d2642',
                    color: s.atual ? 'white' : '#cfe0f3',
                    fontSize: 11, fontWeight: 700, padding: '5px 0', textAlign: 'center',
                    borderLeft: '1px solid rgba(255,255,255,0.35)', whiteSpace: 'nowrap', overflow: 'hidden',
                  }}>
                    {s.dias >= 3 ? `Semana ${s.numero}` : s.dias === 2 ? `S${s.numero}` : s.numero}
                  </Box>
                ))}
              </Box>
              <Box component="tr">
                <Box component="th" sx={{
                  position: 'sticky', left: 0, backgroundColor: '#13375F', color: 'white',
                  padding: '10px 12px', textAlign: 'left', fontSize: 12, zIndex: 2, minWidth: 170,
                }}>
                  Membro da Equipe
                </Box>
                {colunas.map((c) => (
                  <Box key={c.ds} component="th" sx={{
                    backgroundColor: ehFimDeSemana(c) ? '#2C5478' : '#13375F',
                    color: 'white', fontSize: 11, fontFamily: 'monospace',
                    padding: '10px 2px', minWidth: 32, textAlign: 'center',
                    borderBottom: ehHoje(c) ? '3px solid #e94560' : '3px solid transparent',
                    ...(ehSegunda(c) ? { borderLeft: '1px solid rgba(255,255,255,0.35)' } : {}),
                  }}>
                    {inicioSemanaEscolhida && (
                      <Box component="span" sx={{ display: 'block', fontSize: 9, fontFamily: 'inherit', opacity: 0.8 }}>
                        {nomeDiaSemana(c.dow)}
                      </Box>
                    )}
                    {inicioSemanaEscolhida ? `${c.d}/${dayjs(c.ds).month() + 1}` : c.d}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {membros.map((membro) => {
                const editavel = podeEditarLinha(membro);
                return (
                <Box component="tr" key={membro.id}>
                  <Box component="td" sx={{
                    position: 'sticky', left: 0, backgroundColor: '#f4f6f9',
                    padding: '6px 12px', fontSize: 12.5, fontWeight: 600, color: '#1a1a2e',
                    borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', zIndex: 1,
                  }}>
                    {membro.nome_completo}
                  </Box>
                  {colunas.map((c) => {
                    const entradas = mapaCheckins[`${membro.id}-${c.ds}`] || EXISTENTES_VAZIO;
                    const fundoPadrao = ehFimDeSemana(c) ? '#f5f5f5' : 'white';

                    if (entradas.dia_inteiro) {
                      const cfg = CATEGORIA_CONFIG[entradas.dia_inteiro.categoria];
                      return (
                        <Box
                          key={c.ds} component="td"
                          onClick={() => handleAbrirCelula(membro, c.ds)}
                          sx={{
                            border: '1px solid #f0f0f0', textAlign: 'center', cursor: editavel ? 'pointer' : 'default',
                            ...(ehSegunda(c) ? { borderLeft: '1px solid #cbd5e1' } : {}),
                            backgroundColor: cfg?.cor || fundoPadrao, color: cfg?.textColor || '#bbb',
                            fontSize: 10, fontWeight: 700, fontFamily: 'monospace',
                            height: 28, minWidth: 32,
                            outline: ehHoje(c) ? '2px solid #e94560' : 'none', outlineOffset: '-1px',
                            transition: 'opacity 0.1s ease',
                            '&:hover': editavel ? { opacity: 0.7 } : {},
                          }}
                        >
                          {rotuloCelula(entradas.dia_inteiro)}
                        </Box>
                      );
                    }

                    const cfgManha = entradas.manha ? CATEGORIA_CONFIG[entradas.manha.categoria] : null;
                    const cfgTarde = entradas.tarde ? CATEGORIA_CONFIG[entradas.tarde.categoria] : null;
                    const temMeioPeriodo = Boolean(entradas.manha || entradas.tarde);

                    return (
                      <Box
                        key={c.ds} component="td"
                        onClick={() => handleAbrirCelula(membro, c.ds)}
                        sx={{
                          border: '1px solid #f0f0f0', textAlign: 'center', cursor: editavel ? 'pointer' : 'default', p: 0,
                          ...(ehSegunda(c) ? { borderLeft: '1px solid #cbd5e1' } : {}),
                          height: 28, minWidth: 32,
                          outline: ehHoje(c) ? '2px solid #e94560' : 'none', outlineOffset: '-1px',
                          transition: 'opacity 0.1s ease',
                          '&:hover': editavel ? { opacity: 0.7 } : {},
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{
                            flex: 1, backgroundColor: cfgManha?.cor || fundoPadrao, color: cfgManha?.textColor || '#bbb',
                            fontSize: 8, fontWeight: 700, fontFamily: 'monospace',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderBottom: temMeioPeriodo ? '1px solid rgba(0,0,0,0.15)' : 'none',
                          }}>
                            {entradas.manha ? rotuloCelula(entradas.manha) : ''}
                          </Box>
                          <Box sx={{
                            flex: 1, backgroundColor: cfgTarde?.cor || fundoPadrao, color: cfgTarde?.textColor || '#bbb',
                            fontSize: 8, fontWeight: 700, fontFamily: 'monospace',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {entradas.tarde ? rotuloCelula(entradas.tarde) : ''}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog open={Boolean(celulaAberta)} onClose={() => setCelulaAberta(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {celulaAberta?.membroEquipe.nome_completo} — {celulaAberta ? dayjs(celulaAberta.dataStr).format('DD/MM/YYYY') : ''}
        </DialogTitle>
        <DialogContent>
          {erroDialog && <Alert severity="error" sx={{ mb: 2 }}>{erroDialog}</Alert>}

          {ofertaProximoDia ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>Lançamento salvo!</Alert>
              <Typography variant="body2">
                O workshop <b>{ofertaProximoDia.nome}</b> ainda tem {ofertaProximoDia.faltam}{' '}
                {ofertaProximoDia.faltam === 1 ? 'dia' : 'dias'} para lançar. Quer lançar o próximo dia útil agora?
              </Typography>
            </Box>
          ) : ofertaOutroPeriodo ? (
            <Box sx={{ py: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>Lançamento salvo!</Alert>
              <Typography variant="body2">
                A {ofertaOutroPeriodo === 'manha' ? 'Manhã' : 'Tarde'} desse dia ainda está livre. Quer preencher agora?
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth select label="Período" sx={{ mt: 1, mb: 2 }}
                value={form.periodo}
                onChange={(e) => handleMudarPeriodo(e.target.value)}
              >
                <MenuItem value="dia_inteiro">Dia inteiro</MenuItem>
                <MenuItem value="manha">Manhã</MenuItem>
                <MenuItem value="tarde">Tarde</MenuItem>
              </TextField>

              <TextField
                fullWidth select label="Categoria" sx={{ mb: 2 }}
                value={form.categoria}
                onChange={(e) => setForm({
                  ...form, categoria: e.target.value, cliente: '', projeto: '', codigo_atividade: '',
                  tipo_carga: 'consultoria', workshop: '',
                })}
              >
                {Object.entries(CATEGORIA_CONFIG).map(([key, cfg]) => (
                  <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
                ))}
              </TextField>

              {form.categoria === 'consulting' && (
                <>
                  <TextField
                    fullWidth select label="Cliente" sx={{ mb: 2 }}
                    value={form.cliente}
                    onChange={(e) => setForm({ ...form, cliente: e.target.value, projeto: '', workshop: '' })}
                  >
                    {clientesDisponiveis.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.codigo_agenda} — {c.nome}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth select required label="Projeto" sx={{ mb: 2 }}
                    value={form.projeto}
                    onChange={(e) => setForm({ ...form, projeto: e.target.value, workshop: '' })}
                  >
                    {projetosFiltrados.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth select label="Tipo de carga" sx={{ mb: 2 }}
                    value={form.tipo_carga}
                    onChange={(e) => setForm({ ...form, tipo_carga: e.target.value, workshop: '' })}
                  >
                    <MenuItem value="consultoria">Consultoria</MenuItem>
                    <MenuItem value="workshop">Workshop</MenuItem>
                  </TextField>
                  {form.tipo_carga === 'workshop' && form.projeto && precisaEscolherWorkshop && (
                    <>
                      <TextField
                        fullWidth select label="Qual workshop" sx={{ mb: 2 }}
                        value={form.workshop}
                        onChange={(e) => { setForm({ ...form, workshop: e.target.value }); setErroDialog(''); }}
                        helperText={workshopsDoProjeto.total > 0
                          ? `Cada workshop gasta 1 dos vendidos do contrato (${workshopsDoProjeto.livres} livres de ${workshopsDoProjeto.total}).`
                          : 'Cada workshop gasta 1 dos vendidos.'}
                      >
                        {opcoesWorkshop.map((o) => (
                          <MenuItem key={o.valor} value={o.valor} sx={{ whiteSpace: 'normal' }}>{o.rotulo}</MenuItem>
                        ))}
                      </TextField>
                      {opcaoEscolhida && opcaoEscolhida.valor.startsWith('t:') && opcaoEscolhida.dias > 1 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Este workshop tem <strong>{opcaoEscolhida.dias} dias</strong>. Depois de salvar, planeje
                          também mais {opcaoEscolhida.dias - 1} {opcaoEscolhida.dias - 1 === 1 ? 'dia' : 'dias'} na Agenda,
                          escolhendo "Continuar", para o Controle de Carga bater.
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}

              {['business', 'management', 'development'].includes(form.categoria) && (
                <TextField
                  fullWidth select label="Código de atividade" sx={{ mb: 2 }}
                  value={form.codigo_atividade}
                  onChange={(e) => setForm({ ...form, codigo_atividade: e.target.value })}
                >
                  {codigosFiltrados.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.codigo} — {c.descricao}</MenuItem>
                  ))}
                </TextField>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {ofertaProximoDia ? (
            <>
              <Button onClick={() => { setOfertaProximoDia(null); setCelulaAberta(null); }}>Fechar</Button>
              <Button variant="contained" onClick={handleLancarProximoDia} sx={{ backgroundColor: '#e94560' }}>
                Lançar o próximo dia agora
              </Button>
            </>
          ) : ofertaOutroPeriodo ? (
            <>
              <Button onClick={() => setCelulaAberta(null)}>Fechar</Button>
              <Button
                variant="contained"
                onClick={() => { handleMudarPeriodo(ofertaOutroPeriodo); setOfertaOutroPeriodo(null); }}
                sx={{ backgroundColor: '#e94560' }}
              >
                Preencher {ofertaOutroPeriodo === 'manha' ? 'a Manhã' : 'a Tarde'} agora
              </Button>
            </>
          ) : (
            <>
              {celulaAberta?.existentes?.[form.periodo] && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={handleRemoverCelula} disabled={salvando} sx={{ mr: 'auto' }}>
                  Remover
                </Button>
              )}
              <Button onClick={() => setCelulaAberta(null)}>Cancelar</Button>
              <Button variant="contained" onClick={handleSalvarCelula} disabled={salvando} sx={{ backgroundColor: '#13375F' }}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
